import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { useAuth } from '../context/AuthContext';
import {
  Shield, Eye, EyeOff, AlertCircle, CheckCircle,
  ArrowRight, ChevronRight, User,
} from 'lucide-react';

/* ── password strength ───────────────────────────────────────────────── */
function PasswordStrength({ password }) {
  if (!password) return null;
  const checks = [
    { label: 'At least 8 characters',    ok: password.length >= 8 },
    { label: 'Contains a number',         ok: /\d/.test(password) },
    { label: 'Contains uppercase letter', ok: /[A-Z]/.test(password) },
    { label: 'Contains special character',ok: /[^A-Za-z0-9]/.test(password) },
  ];
  const score = checks.filter(c => c.ok).length;
  const colors = ['', 'bg-red-500', 'bg-amber-500', 'bg-yellow-400', 'bg-green-500'];
  const labels = ['', 'Weak', 'Fair', 'Good', 'Strong'];

  return (
    <div className="mt-2 space-y-2">
      <div className="flex gap-1 h-1">
        {[1, 2, 3, 4].map(i => (
          <div
            key={i}
            className={`flex-1 rounded-full transition-all duration-300 ${i <= score ? colors[score] : 'bg-white/[0.08]'}`}
          />
        ))}
      </div>
      <div className="flex items-center justify-between">
        <div className="flex flex-wrap gap-x-3 gap-y-0.5">
          {checks.map(({ label, ok }) => (
            <span key={label} className={`flex items-center gap-1 text-[10px] ${ok ? 'text-green-400' : 'text-slate-600'}`}>
              <CheckCircle size={9} className={ok ? 'opacity-100' : 'opacity-30'} />
              {label}
            </span>
          ))}
        </div>
        {score > 0 && (
          <span className={`text-[10px] font-bold flex-shrink-0 ml-2 ${['', 'text-red-400', 'text-amber-400', 'text-yellow-400', 'text-green-400'][score]}`}>
            {labels[score]}
          </span>
        )}
      </div>
    </div>
  );
}

/* ── field component ─────────────────────────────────────────────────── */
function Field({ label, id, type, value, onChange, placeholder, error, disabled, suffix, hint }) {
  return (
    <div>
      <label htmlFor={id} className="block text-xs font-semibold text-slate-400 mb-1.5">
        {label}
      </label>
      <div className="relative">
        <input
          id={id}
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete={
            id === 'email'   ? 'email' :
            id === 'name'    ? 'name' :
            id === 'password'? 'new-password' : 'new-password'
          }
          className={[
            'w-full bg-white/[0.04] border rounded-xl px-4 py-3 text-sm text-slate-200',
            'placeholder-slate-600 outline-none transition-colors',
            suffix ? 'pr-11' : '',
            error
              ? 'border-red-500/50 focus:border-red-500/70'
              : 'border-white/[0.08] focus:border-[#00E5FF]/50',
            disabled ? 'opacity-50 cursor-not-allowed' : '',
          ].join(' ')}
        />
        {suffix && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">{suffix}</div>
        )}
      </div>
      {error && (
        <p className="mt-1.5 text-xs text-red-400 flex items-center gap-1">
          <AlertCircle size={11} /> {error}
        </p>
      )}
      {hint && !error && (
        <p className="mt-1 text-[10px] text-slate-600">{hint}</p>
      )}
    </div>
  );
}

/* ── divider ─────────────────────────────────────────────────────────── */
function Divider() {
  return (
    <div className="flex items-center gap-3 my-5">
      <div className="flex-1 h-px bg-white/[0.07]" />
      <span className="text-xs text-slate-600 font-medium">or register with email</span>
      <div className="flex-1 h-px bg-white/[0.07]" />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   Register page
═══════════════════════════════════════════════════════════════════════ */
export default function Register() {
  const navigate  = useNavigate();
  const { register, googleLogin, user, error: ctxError, clearError } = useAuth();

  const [name,       setName]       = useState('');
  const [email,      setEmail]      = useState('');
  const [password,   setPassword]   = useState('');
  const [confirm,    setConfirm]    = useState('');
  const [showPwd,    setShowPwd]    = useState(false);
  const [showConf,   setShowConf]   = useState(false);
  const [agreed,     setAgreed]     = useState(false);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState('');
  const [fieldErr,   setFieldErr]   = useState({});

  useEffect(() => { if (user) navigate('/dashboard', { replace: true }); }, [user, navigate]);
  useEffect(() => { if (ctxError) { setError(ctxError); clearError(); } }, [ctxError, clearError]);

  /* ── validation ─────────────────────────────────────────────────── */
  const validate = () => {
    const errs = {};
    if (!email)                                          errs.email    = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errs.email    = 'Enter a valid email';
    if (!password)                                       errs.password = 'Password is required';
    else if (password.length < 8)                        errs.password = 'Password must be at least 8 characters';
    if (!confirm)                                        errs.confirm  = 'Please confirm your password';
    else if (password !== confirm)                       errs.confirm  = 'Passwords do not match';
    if (!agreed)                                         errs.agreed   = 'You must accept the terms to continue';
    setFieldErr(errs);
    return Object.keys(errs).length === 0;
  };

  /* ── submit ─────────────────────────────────────────────────────── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!validate()) return;
    setLoading(true);
    const result = await register(email, password, name);
    setLoading(false);
    if (result.success) {
      navigate('/dashboard', { replace: true });
    } else {
      setError(result.error || 'Registration failed. Please try again.');
    }
  };

  /* ── Google success ─────────────────────────────────────────────── */
  const handleGoogleSuccess = async (credentialResponse) => {
    setError('');
    setLoading(true);
    const result = await googleLogin(credentialResponse.credential);
    setLoading(false);
    if (result.success) {
      navigate('/dashboard', { replace: true });
    } else {
      setError(result.error || 'Google sign-up failed.');
    }
  };

  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  return (
    <div className="min-h-screen bg-[#0B0F19] flex items-center justify-center p-4 relative overflow-hidden">

      {/* Background */}
      <div className="absolute top-1/4 -left-40 w-96 h-96 bg-[#7C3AED]/6 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-1/4 -right-40 w-96 h-96 bg-[#00E5FF]/6 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute inset-0 hero-grid opacity-30 pointer-events-none" />

      <div className="relative w-full max-w-md">

        <div className="glass-card cyber-border p-8 sm:p-10 shadow-2xl shadow-black/50">

          {/* Logo */}
          <div className="flex items-center justify-center mb-8">
            <Link to="/" className="flex items-center gap-2.5 group">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#00E5FF] to-[#7C3AED] flex items-center justify-center shadow-lg group-hover:shadow-[#00E5FF]/30 transition-shadow">
                <Shield size={17} className="text-white" />
              </div>
              <span className="text-white font-bold text-lg tracking-tight">
                Zenith<span className="text-[#00E5FF]">Cipher</span>
              </span>
            </Link>
          </div>

          {/* Heading */}
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-white mb-1.5">Create your account</h1>
            <p className="text-slate-500 text-sm">
              Free plan — no credit card required
            </p>
          </div>

          {/* Global error */}
          {error && (
            <div className="flex items-start gap-2.5 bg-red-500/10 border border-red-500/25 text-red-400 rounded-xl px-4 py-3 mb-5 text-sm">
              <AlertCircle size={15} className="flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {/* Google button */}
          {googleClientId ? (
            <>
              <div className="flex justify-center mb-1">
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={() => setError('Google sign-up was cancelled or failed.')}
                  useOneTap={false}
                  theme="filled_black"
                  shape="rectangular"
                  size="large"
                  width="368"
                  text="signup_with"
                />
              </div>
              <Divider />
            </>
          ) : (
            <>
              <div className="flex items-center justify-center gap-2 w-full border border-white/[0.08] bg-white/[0.03] rounded-xl py-3 mb-4 text-sm text-slate-500 cursor-default select-none">
                <svg width="18" height="18" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Sign up with Google
                <span className="text-[10px] ml-1 text-slate-600">(configure REACT_APP_GOOGLE_CLIENT_ID)</span>
              </div>
              <Divider />
            </>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} noValidate className="space-y-4">

            <Field
              label="Full Name (optional)"
              id="name"
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Alex Johnson"
              disabled={loading}
              suffix={<User size={14} className="text-slate-600" />}
            />

            <Field
              label="Email Address"
              id="email"
              type="email"
              value={email}
              onChange={e => { setEmail(e.target.value); setFieldErr(f => ({ ...f, email: '' })); }}
              placeholder="you@example.com"
              error={fieldErr.email}
              disabled={loading}
            />

            <div>
              <Field
                label="Password"
                id="password"
                type={showPwd ? 'text' : 'password'}
                value={password}
                onChange={e => { setPassword(e.target.value); setFieldErr(f => ({ ...f, password: '' })); }}
                placeholder="Min. 8 characters"
                error={fieldErr.password}
                disabled={loading}
                suffix={
                  <button type="button" tabIndex={-1} onClick={() => setShowPwd(v => !v)} className="text-slate-600 hover:text-slate-400 transition-colors">
                    {showPwd ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                }
              />
              <PasswordStrength password={password} />
            </div>

            <Field
              label="Confirm Password"
              id="confirm"
              type={showConf ? 'text' : 'password'}
              value={confirm}
              onChange={e => { setConfirm(e.target.value); setFieldErr(f => ({ ...f, confirm: '' })); }}
              placeholder="Re-enter your password"
              error={fieldErr.confirm}
              disabled={loading}
              suffix={
                <button type="button" tabIndex={-1} onClick={() => setShowConf(v => !v)} className="text-slate-600 hover:text-slate-400 transition-colors">
                  {showConf ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              }
            />

            {/* Terms checkbox */}
            <div>
              <label className="flex items-start gap-2.5 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={e => { setAgreed(e.target.checked); setFieldErr(f => ({ ...f, agreed: '' })); }}
                  className="w-3.5 h-3.5 accent-[#00E5FF] mt-0.5 flex-shrink-0 cursor-pointer"
                />
                <span className="text-xs text-slate-500 leading-relaxed group-hover:text-slate-400 transition-colors">
                  I agree to the{' '}
                  <Link to="/terms" className="text-[#00E5FF] hover:underline" target="_blank" rel="noopener noreferrer">Terms of Service</Link>
                  {' '}and{' '}
                  <Link to="/privacy" className="text-[#00E5FF] hover:underline" target="_blank" rel="noopener noreferrer">Privacy Policy</Link>
                </span>
              </label>
              {fieldErr.agreed && (
                <p className="mt-1.5 text-xs text-red-400 flex items-center gap-1">
                  <AlertCircle size={11} /> {fieldErr.agreed}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 bg-[#00E5FF] hover:bg-[#00cce5] disabled:opacity-50 text-black font-bold py-3.5 rounded-xl transition-all text-sm shadow-lg shadow-[#00E5FF]/20 hover:shadow-[#00E5FF]/35 hover:-translate-y-0.5 group mt-2"
            >
              {loading ? (
                <>
                  <span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                  Creating account…
                </>
              ) : (
                <>
                  Create Free Account
                  <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </button>
          </form>

          {/* What you get */}
          <div className="mt-6 pt-5 border-t border-white/[0.05]">
            <p className="text-[10px] text-slate-600 text-center mb-3">Included in the free plan:</p>
            <div className="grid grid-cols-2 gap-1.5">
              {['10 scans per day', '1 API key', 'Risk scoring', '7-day history'].map(item => (
                <div key={item} className="flex items-center gap-1.5 text-[10px] text-slate-500">
                  <CheckCircle size={10} className="text-[#00E5FF] flex-shrink-0" />
                  {item}
                </div>
              ))}
            </div>
          </div>

          <p className="text-center text-sm text-slate-500 mt-5">
            Already have an account?{' '}
            <Link to="/login" className="text-[#00E5FF] hover:text-[#00cce5] font-semibold transition-colors">
              Sign in <ChevronRight size={12} className="inline" />
            </Link>
          </p>
        </div>

        <p className="text-center text-xs text-slate-700 mt-5 flex items-center justify-center gap-1.5">
          <Shield size={11} className="text-[#00E5FF]/50" />
          Protected by ZenithCipher enterprise-grade security
        </p>
      </div>
    </div>
  );
}
